import { useMemo, useState } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, CartesianGrid } from "recharts";
import { startOfWeek, subDays, subWeeks, subMonths, format, parseISO, isSameDay, isSameWeek, isSameMonth } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { useCollection } from "@/hooks/useCollection";
import { rupiah, sum, INCOME_TYPES } from "@/lib/format";
import { Stat, Section } from "./shared";

const RANGES = { day: "Harian", week: "Mingguan", month: "Bulanan" };
const same = { day: isSameDay, week: (a, b) => isSameWeek(a, b, { weekStartsOn: 1 }), month: isSameMonth };

function buckets(range) {
  const now = new Date();
  if (range === "day") return Array.from({ length: 7 }, (_, i) => subDays(now, 6 - i)).map((d) => ({ d, label: format(d, "EEE", { locale: localeId }) }));
  if (range === "week") return Array.from({ length: 8 }, (_, i) => startOfWeek(subWeeks(now, 7 - i), { weekStartsOn: 1 })).map((d) => ({ d, label: format(d, "d/M") }));
  return Array.from({ length: 6 }, (_, i) => subMonths(now, 5 - i)).map((d) => ({ d, label: format(d, "MMM", { locale: localeId }) }));
}

export default function Overview() {
  const [range, setRange] = useState("month");
  const { items: inc } = useCollection("incomes");
  const { items: exp } = useCollection("expenses");
  const now = new Date();
  const inRange = (arr) => arr.filter((x) => same[range](parseISO(x.date), now));
  const curInc = inRange(inc), curExp = inRange(exp);

  const chart = useMemo(() => buckets(range).map(({ d, label }) => ({
    label,
    Pemasukan: sum(inc.filter((x) => same[range](parseISO(x.date), d))),
    Pengeluaran: sum(exp.filter((x) => same[range](parseISO(x.date), d))),
  })), [inc, exp, range]);

  return (
    <div className="space-y-4" data-testid="duitku-overview">
      <div className="flex gap-2">
        {Object.entries(RANGES).map(([k, l]) => (
          <button key={k} onClick={() => setRange(k)} className={`chip ${range === k ? "chip-on" : "chip-off"}`} data-testid={`overview-range-${k}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={`Pemasukan ${RANGES[range].toLowerCase()}`} value={rupiah(sum(curInc))} tone="text-emerald-500" testid="overview-income-total" />
        <Stat label="Pengeluaran" value={rupiah(sum(curExp))} tone="text-rose-500" testid="overview-expense-total" />
        <Stat label="Sisa uangku" value={rupiah(sum(curInc) - sum(curExp))} tone="text-pink-500" testid="overview-net" />
        <Stat label="Jumlah catatan" value={curInc.length + curExp.length} testid="overview-count" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Section title="Grafik uangku" className="lg:col-span-2">
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#FCE7F3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 16, border: "1px solid #FBCFE8" }} />
                <Bar dataKey="Pemasukan" fill="#F472B6" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Pengeluaran" fill="#7DD3FC" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Dari mana uangku">
          <div className="space-y-3">
            {Object.entries(INCOME_TYPES).map(([k, l]) => {
              const v = sum(curInc.filter((x) => x.type === k));
              const pct = sum(curInc) ? (v / sum(curInc)) * 100 : 0;
              return (
                <div key={k} data-testid={`overview-source-${k}`}>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">{l}</span><span className="font-semibold text-gray-700">{rupiah(v)}</span></div>
                  <div className="h-2 bg-pink-50 rounded-full mt-1 overflow-hidden"><div className="h-full bg-pink-400 rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
}
